import React from 'react';

/**
 * Concentric rings — one per series, each a percentage of the same 0-100 sweep.
 *
 * Rings share a scale and a centre, so the eye compares them directly: that is the one
 * job this shape does better than bars, and the reason to reach for it when the question
 * is "which of these did best" rather than "how many". Magnitude is NOT encoded — an outer
 * ring is not bigger, only earlier — so pair it with the counts, never use it alone.
 *
 * Pure SVG rather than a chart library: the ring spacing, the rounded caps and the centre
 * label all need to land exactly, and a dependency buys nothing at this size.
 */
export function RadialRings({
  rings,
  size = 188,
  thickness = 13,
  gap = 6,
  centerValue,
  centerLabel,
  track = 'rgba(70,80,72,0.07)',
}) {
  const c = size / 2;
  const radiusAt = (i) => c - thickness / 2 - i * (thickness + gap);
  // Rings the circle can actually hold; the rest are dropped, so the hole below is real.
  const drawn = rings.filter((_, i) => radiusAt(i) > thickness);

  /**
   * The empty disc inside the innermost ring. The centre text is confined to it — laid over
   * the whole box instead, a two-word label runs under the rings and the number stops being
   * readable, which is exactly what it used to do.
   */
  const hole = Math.max(0, (radiusAt(Math.max(0, drawn.length - 1)) - thickness / 2) * 2);
  const valueSize = Math.round(Math.min(26, Math.max(15, hole * 0.3)));
  const labelSize = Math.round(Math.min(11, Math.max(8.5, hole * 0.115)));

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img">
        {/* Start every sweep at twelve o'clock and run clockwise, the way a dial reads. */}
        <g transform={`rotate(-90 ${c} ${c})`}>
          {drawn.map((r, i) => {
            const radius = radiusAt(i);
            const circumference = 2 * Math.PI * radius;
            const value = Math.max(0, Math.min(100, Number(r.value) || 0));
            return (
              <g key={r.key}>
                <circle cx={c} cy={c} r={radius} fill="none" stroke={track} strokeWidth={thickness} />
                {/* Zero draws nothing: a round cap on an empty arc still paints a dot, and a
                    dot on the track reads as a small non-zero value. */}
                {value > 0 && (
                  <circle
                    cx={c}
                    cy={c}
                    r={radius}
                    fill="none"
                    stroke={r.color}
                    strokeWidth={thickness}
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={circumference * (1 - value / 100)}
                    style={{ transition: 'stroke-dashoffset 0.9s cubic-bezier(0.16,1,0.3,1)' }}
                  >
                    <title>{`${r.label}: ${value}%`}</title>
                  </circle>
                )}
              </g>
            );
          })}
        </g>
      </svg>

      {(centerValue != null || centerLabel) && (
        <div
          className="pointer-events-none absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center text-center"
          style={{ width: hole, height: hole }}
        >
          {centerValue != null && (
            <span
              className="mono font-bold leading-none text-cos-ink"
              style={{ fontSize: `${valueSize}px` }}
            >
              {centerValue}
            </span>
          )}
          {centerLabel && (
            <span
              className="mt-1 font-medium uppercase leading-[1.2] tracking-[0.06em] text-cos-ink-muted"
              style={{ fontSize: `${labelSize}px` }}
            >
              {centerLabel}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default RadialRings;
